import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "meta.selectedAdAccount";

const normalizeAccount = (item = {}) => ({
    key: item.key || "",
    name: item.name || item.adAccountId || "Cuenta publicitaria",
    adAccountId: item.adAccountId || "",
    isDefault: !!item.isDefault,
});

export const readStoredMetaAdAccount = () => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
        return null;
    }
};

export const writeStoredMetaAdAccount = (account) => {
    try {
        if (!account) {
            window.localStorage.removeItem(STORAGE_KEY);
            return;
        }

        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
            key: account.key || "",
            adAccountId: account.adAccountId || "",
        }));
    } catch {
        // no-op
    }
};

export const buildMetaAccountParams = (account) => {
    const params = new URLSearchParams();
    if (account?.adAccountId) params.set("adAccountId", account.adAccountId);
    if (account?.key) params.set("adAccountKey", account.key);
    return params;
};

export const appendMetaAccountToUrl = (baseUrl, account) => {
    const params = buildMetaAccountParams(account);
    if ([...params.keys()].length === 0) {
        return baseUrl;
    }

    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}${params.toString()}`;
};

export const fetchMetaAdAccounts = async () => {
    const response = await fetch("/api/meta/adaccounts");
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Error ${response.status}`);
    }

    const payload = await response.json();
    return Array.isArray(payload) ? payload.map(normalizeAccount) : [];
};

export const useMetaAdAccounts = () => {
    const [accounts, setAccounts] = useState([]);
    const [selectedKey, setSelectedKey] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError("");
            try {
                const rows = await fetchMetaAdAccounts();
                if (cancelled) return;

                setAccounts(rows);
                const stored = readStoredMetaAdAccount();
                const preferred = rows.find((item) =>
                    (stored?.adAccountId && item.adAccountId === stored.adAccountId)
                    || (stored?.key && item.key === stored.key))
                    || rows.find((item) => item.isDefault)
                    || rows[0]
                    || null;

                setSelectedKey(preferred?.key || "");
                writeStoredMetaAdAccount(preferred);
            } catch (err) {
                if (cancelled) return;
                setAccounts([]);
                setSelectedKey("");
                setError(err?.message || "No se pudieron cargar las cuentas publicitarias.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const selectedAccount = useMemo(() =>
        accounts.find((item) => item.key === selectedKey)
        || accounts.find((item) => item.isDefault)
        || accounts[0]
        || null,
    [accounts, selectedKey]);

    const selectAccount = useCallback((nextKey) => {
        setSelectedKey(nextKey);
        const nextAccount = accounts.find((item) => item.key === nextKey)
            || accounts.find((item) => item.adAccountId === nextKey)
            || null;
        writeStoredMetaAdAccount(nextAccount);
    }, [accounts]);

    return {
        accounts,
        selectedAccount,
        selectedKey: selectedAccount?.key || "",
        loading,
        error,
        selectAccount,
    };
};
